import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/api/axios';
import { Loader2, Plus, Trash2, Upload, Star, Save } from 'lucide-react';

const EMPRESA = 'complementos';

const Uploader = ({ onUploaded, testid }) => {
  const [subiendo, setSubiendo] = useState(false);
  const subir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      setSubiendo(true);
      const { data } = await api.post(`/tienda/${EMPRESA}/admin/upload-imagen`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(data.url);
      toast.success('Imagen subida');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error subiendo imagen'); }
    finally { setSubiendo(false); e.target.value = ''; }
  };
  return (
    <label data-testid={testid ? `${testid}-btn` : undefined} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-blue-500/50 text-blue-600 hover:bg-blue-500/10 cursor-pointer transition-colors">
      {subiendo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
      {subiendo ? 'Subiendo…' : 'Subir imagen'}
      <input type="file" accept="image/*" className="hidden" onChange={subir} data-testid={testid} />
    </label>
  );
};

const Seccion = ({ titulo, hint, children }) => (
  <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
    <p className="font-black text-sm">{titulo}</p>
    <p className="text-xs opacity-60 mb-3">{hint}</p>
    {children}
  </div>
);

export default function TiendaPortadaAdmin() {
  const [cfg, setCfg] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [nuevoSku, setNuevoSku] = useState('');
  const [nuevoPrecio, setNuevoPrecio] = useState('');
  const [nuevoPct, setNuevoPct] = useState('');
  const [resultados, setResultados] = useState([]);

  const buscarProducto = (texto) => {
    clearTimeout(window.__skuTimer);
    if (!texto || texto.trim().length < 2) { setResultados([]); return; }
    window.__skuTimer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/tienda/${EMPRESA}/catalogo`, { params: { q: texto.trim(), per_page: 8 } });
        setResultados(data.data || []);
      } catch { setResultados([]); }
    }, 350);
  };
  const [nuevaEtiqueta, setNuevaEtiqueta] = useState('OFERTA');

  useEffect(() => {
    api.get(`/tienda/${EMPRESA}/home`).then(({ data }) => {
      setCfg({ banners: data.banners, ideas: data.ideas, testimonios: data.testimonios, ofertas: data.ofertas_cfg, marcas: data.marcas || [] });
    }).catch(() => toast.error('No se pudo cargar la portada'));
  }, []);

  const guardar = async () => {
    try {
      setGuardando(true);
      await api.put(`/tienda/${EMPRESA}/admin/home`, cfg);
      toast.success('Portada guardada. Los cambios ya se ven en la tienda.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error guardando'); }
    finally { setGuardando(false); }
  };

  const upd = (campo, valor) => setCfg((c) => ({ ...c, [campo]: valor }));

  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-4" data-testid="portada-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm opacity-70">Personaliza la página de inicio de tu tienda en línea.</p>
        <button onClick={guardar} disabled={guardando} data-testid="portada-guardar"
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-black text-xs px-5 py-2.5 rounded-lg transition-colors">
          {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar portada
        </button>
      </div>

      <Seccion titulo="🖼️ Banners de encabezado" hint="Imágenes grandes al inicio de la tienda (rotan solas). Ideal: fotos de tus productos en uso, 1600×600 aprox. Máx 6.">
        <div className="space-y-2">
          {cfg.banners.map((b, i) => (
            <div key={i} className="flex items-center gap-3 border border-slate-200 dark:border-slate-700 rounded-lg p-2" data-testid={`banner-item-${i}`}>
              <img src={b.url} alt="" className="w-24 h-12 object-cover rounded" />
              <input value={b.titulo || ''} onChange={(e) => upd('banners', cfg.banners.map((x, j) => j === i ? { ...x, titulo: e.target.value } : x))}
                placeholder="Título (opcional)" className="flex-1 min-w-0 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs outline-none" />
              <input value={b.subtitulo || ''} onChange={(e) => upd('banners', cfg.banners.map((x, j) => j === i ? { ...x, subtitulo: e.target.value } : x))}
                placeholder="Subtítulo (opcional)" className="flex-1 min-w-0 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs outline-none" />
              <button onClick={() => upd('banners', cfg.banners.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-500/10 p-1.5 rounded" data-testid={`banner-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {cfg.banners.length < 6 && <Uploader testid="banner-upload" onUploaded={(url) => upd('banners', [...cfg.banners, { url, titulo: '', subtitulo: '' }])} />}
        </div>
      </Seccion>

      <Seccion titulo="🏆 Marcas que manejamos" hint="Logos de las marcas que vendes: dan confianza al comprador. Ideal: logo sobre fondo blanco o transparente. Máx 12.">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-2">
          {(cfg.marcas || []).map((m, i) => (
            <div key={i} className="relative border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white" data-testid={`marca-item-${i}`}>
              <img src={m.url} alt={m.nombre || 'Marca'} className="w-full h-16 object-contain p-1.5" />
              <input value={m.nombre || ''} onChange={(e) => upd('marcas', cfg.marcas.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x))}
                placeholder="Nombre (opcional)" className="w-full bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-1.5 py-1 text-[10px] outline-none text-slate-700 dark:text-slate-200" />
              <button onClick={() => upd('marcas', cfg.marcas.filter((_, j) => j !== i))} data-testid={`marca-del-${i}`}
                className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white p-1 rounded"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
        {(cfg.marcas || []).length < 12 && <Uploader testid="marca-upload" onUploaded={(url) => upd('marcas', [...(cfg.marcas || []), { url, nombre: '' }])} />}
      </Seccion>

      <Seccion titulo="💡 Ideas y usos de nuestros herrajes" hint="Galería de fotos con ejemplos reales de cómo se usan tus productos. Máx 12.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {cfg.ideas.map((it, i) => (
            <div key={i} className="relative border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden" data-testid={`idea-item-${i}`}>
              <img src={it.url} alt="" className="w-full h-24 object-cover" />
              <input value={it.caption || ''} onChange={(e) => upd('ideas', cfg.ideas.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))}
                placeholder="Descripción corta" className="w-full bg-transparent border-t border-slate-200 dark:border-slate-700 px-2 py-1 text-[11px] outline-none" />
              <button onClick={() => upd('ideas', cfg.ideas.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded" data-testid={`idea-del-${i}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
        {cfg.ideas.length < 12 && <Uploader testid="idea-upload" onUploaded={(url) => upd('ideas', [...cfg.ideas, { url, caption: '' }])} />}
      </Seccion>

      <Seccion titulo="⭐ Testimonios" hint="Opiniones de clientes que se muestran en la tienda. Edita, agrega o elimina.">
        <div className="space-y-2">
          {cfg.testimonios.map((t, i) => (
            <div key={t.id || i} className="flex items-start gap-2 border border-slate-200 dark:border-slate-700 rounded-lg p-2" data-testid={`testi-item-${i}`}>
              <div className="flex-1 space-y-1.5 min-w-0">
                <input value={t.nombre} onChange={(e) => upd('testimonios', cfg.testimonios.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x))}
                  placeholder="Nombre del cliente" className="w-full bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs font-bold outline-none" />
                <textarea value={t.texto} rows={2} onChange={(e) => upd('testimonios', cfg.testimonios.map((x, j) => j === i ? { ...x, texto: e.target.value } : x))}
                  placeholder="Opinión…" className="w-full bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs outline-none resize-none" />
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => upd('testimonios', cfg.testimonios.map((x, j) => j === i ? { ...x, estrellas: n } : x))} type="button">
                      <Star className={`w-4 h-4 ${n <= (t.estrellas || 5) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => upd('testimonios', cfg.testimonios.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-500/10 p-1.5 rounded" data-testid={`testi-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {cfg.testimonios.length < 10 && (
            <button onClick={() => upd('testimonios', [...cfg.testimonios, { id: `t${Date.now()}`, nombre: '', texto: '', estrellas: 5 }])} data-testid="testi-add"
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg border border-blue-500/50 text-blue-600 hover:bg-blue-500/10 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Agregar testimonio
            </button>
          )}
        </div>
      </Seccion>

      <Seccion titulo="🔥 Productos en oferta" hint="Escribe el SKU y elige: precio rebajado directo (ej: 4.00) O descuento en % (ej: 10 → de Q5.00 baja a Q4.50). El % tiene prioridad. En la tienda se ve el precio normal tachado y el rebajado se cobra en el carrito. Máx 8.">
        <div className="space-y-2">
          {cfg.ofertas.map((o, i) => (
            <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-lg p-2 space-y-1.5" data-testid={`oferta-item-${i}`}>
              <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold flex-1">{o.sku}</span>
              <input type="number" min="0" step="0.01" value={o.precio_oferta || ''} onChange={(e) => upd('ofertas', cfg.ofertas.map((x, j) => j === i ? { ...x, precio_oferta: e.target.value } : x))}
                placeholder="Precio Q" title="Precio rebajado directo (ej: 4.00)" data-testid={`oferta-precio-${i}`}
                className="w-24 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs outline-none" />
              <input type="number" min="0" max="99" step="1" value={o.descuento_pct || ''} onChange={(e) => upd('ofertas', cfg.ofertas.map((x, j) => j === i ? { ...x, descuento_pct: e.target.value } : x))}
                placeholder="% desc" title="Descuento en % sobre el precio normal (ej: 10). Si lo llenas, tiene prioridad sobre el precio directo." data-testid={`oferta-pct-${i}`}
                className="w-20 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs outline-none" />
              <input value={o.etiqueta || ''} onChange={(e) => upd('ofertas', cfg.ofertas.map((x, j) => j === i ? { ...x, etiqueta: e.target.value } : x))}
                placeholder="Etiqueta" className="w-32 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs outline-none" />
              <button onClick={() => upd('ofertas', cfg.ofertas.filter((_, j) => j !== i))} className="text-red-500 hover:bg-red-500/10 p-1.5 rounded" data-testid={`oferta-del-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center gap-2 text-[11px] flex-wrap">
                <span className="opacity-60 font-bold">Imagen diseñada:</span>
                {o.imagen ? (
                  <>
                    <img src={o.imagen} alt="" className="w-16 h-9 object-cover rounded border border-slate-200 dark:border-slate-600" data-testid={`oferta-img-preview-${i}`} />
                    <button onClick={() => upd('ofertas', cfg.ofertas.map((x, j) => j === i ? { ...x, imagen: '' } : x))} data-testid={`oferta-img-del-${i}`}
                      className="text-red-500 hover:bg-red-500/10 px-1.5 py-1 rounded font-bold">Quitar</button>
                  </>
                ) : (
                  <>
                    <Uploader testid={`oferta-img-upload-${i}`} onUploaded={(url) => upd('ofertas', cfg.ofertas.map((x, j) => j === i ? { ...x, imagen: url } : x))} />
                    <span className="opacity-50">Opcional: tu arte promocional. Se muestra en el banner rotativo de ofertas y en la tarjeta del producto.</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 text-[11px] flex-wrap">
                <span className="opacity-60 font-bold">Vigencia:</span>
                <input type="date" value={o.desde || ''} onChange={(e) => upd('ofertas', cfg.ofertas.map((x, j) => j === i ? { ...x, desde: e.target.value } : x))}
                  data-testid={`oferta-desde-${i}`} title="Fecha de inicio (opcional)"
                  className="bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-[11px] outline-none" />
                <span className="opacity-50">a</span>
                <input type="date" value={o.hasta || ''} onChange={(e) => upd('ofertas', cfg.ofertas.map((x, j) => j === i ? { ...x, hasta: e.target.value } : x))}
                  data-testid={`oferta-hasta-${i}`} title="Fecha de fin (opcional)"
                  className="bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-[11px] outline-none" />
                <span className="opacity-50">vacías = siempre activa</span>
                {o.hasta && o.hasta < new Date().toISOString().slice(0, 10) && <span className="text-red-500 font-black">VENCIDA</span>}
                {o.desde && o.desde > new Date().toISOString().slice(0, 10) && <span className="text-amber-500 font-black">PROGRAMADA</span>}
              </div>
            </div>
          ))}
          {cfg.ofertas.length < 8 && (
            <div className="relative">
              <div className="flex gap-2">
                <input value={nuevoSku} onChange={(e) => { setNuevoSku(e.target.value); buscarProducto(e.target.value); }} placeholder="Busca por nombre o SKU…" data-testid="oferta-sku-input"
                  className="flex-1 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-2 text-xs outline-none" />
                <input type="number" min="0" step="0.01" value={nuevoPrecio} onChange={(e) => setNuevoPrecio(e.target.value)} placeholder="Precio Q" data-testid="oferta-precio-input"
                  className="w-24 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-2 text-xs outline-none" />
                <input type="number" min="0" max="99" step="1" value={nuevoPct} onChange={(e) => setNuevoPct(e.target.value)} placeholder="% desc" data-testid="oferta-pct-input"
                  className="w-20 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-2 text-xs outline-none" />
                <input value={nuevaEtiqueta} onChange={(e) => setNuevaEtiqueta(e.target.value)} placeholder="Etiqueta" data-testid="oferta-etiqueta-input"
                  className="w-28 bg-transparent border border-slate-300 dark:border-slate-600 rounded px-2 py-2 text-xs outline-none" />
                <button onClick={() => { if (!nuevoSku.trim()) return; upd('ofertas', [...cfg.ofertas, { sku: nuevoSku.trim(), etiqueta: nuevaEtiqueta.trim(), precio_oferta: nuevoPrecio, descuento_pct: nuevoPct }]); setNuevoSku(''); setNuevoPrecio(''); setNuevoPct(''); setResultados([]); }}
                  data-testid="oferta-add" className="flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Agregar
                </button>
              </div>
              {resultados.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="oferta-sku-resultados">
                  {resultados.map((r) => (
                    <button key={r.sku} onClick={() => { setNuevoSku(r.sku); setResultados([]); }} data-testid={`oferta-sku-opcion-${r.sku}`}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-blue-500/10 border-b border-slate-100 dark:border-slate-700 last:border-0">
                      <span className="font-mono font-black">{r.sku}</span> · {r.descripcion} <span className="opacity-60">· Q{Number(r.precio).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Seccion>
    </div>
  );
}
